import { useMemo, useRef, useState, useEffect } from "react";

/**
 * Renders an image with SVG bounding boxes overlaid at the correct scale.
 * Roboflow returns center-point coordinates (x, y) with width, height in
 * original-image pixel space.
 */
export default function BoundingBoxOverlay({
  imageSrc,
  predictions = [],
  imageWidth,
  imageHeight,
  showBoxes = true,
  showLabels = true,
  hoveredIndex = null,
  onHover,
  testId = "xray-viewer",
}) {
  const wrapperRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const ro = new ResizeObserver(() => {
      setDisplaySize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const boxes = useMemo(() => {
    if (!imageWidth || !imageHeight) return [];
    return predictions.map((p, i) => {
      const conf = p.confidence ?? 0;
      const color =
        conf >= 0.7 ? "#f43f5e" : conf >= 0.4 ? "#f59e0b" : "#818cf8";
      const x = (p.x - p.width / 2);
      const y = (p.y - p.height / 2);
      return {
        i,
        x,
        y,
        w: p.width,
        h: p.height,
        label: `${p.class} ${(conf * 100).toFixed(0)}%`,
        color,
      };
    });
  }, [predictions, imageWidth, imageHeight]);

  return (
    <div
      ref={wrapperRef}
      className="relative w-full bg-black rounded-xl overflow-hidden border border-zinc-800 flex items-center justify-center"
      data-testid={testId}
      style={{ aspectRatio: imageWidth && imageHeight ? `${imageWidth}/${imageHeight}` : "4/3" }}
    >
      {imageSrc ? (
        <>
          <img
            src={imageSrc}
            alt="Chest X-ray"
            className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
            draggable={false}
          />
          {showBoxes && imageWidth && imageHeight && (
            <svg
              viewBox={`0 0 ${imageWidth} ${imageHeight}`}
              preserveAspectRatio="xMidYMid meet"
              className="absolute inset-0 w-full h-full pointer-events-none"
            >
              {boxes.map((b) => {
                const active = hoveredIndex === b.i;
                return (
                  <g key={b.i} pointerEvents="auto">
                    <rect
                      x={b.x}
                      y={b.y}
                      width={b.w}
                      height={b.h}
                      fill={active ? `${b.color}33` : "transparent"}
                      stroke={b.color}
                      strokeWidth={active ? 4 : 2.5}
                      strokeDasharray={active ? "0" : "0"}
                      onMouseEnter={() => onHover && onHover(b.i)}
                      onMouseLeave={() => onHover && onHover(null)}
                      style={{ cursor: "pointer" }}
                    />
                    {showLabels && (
                      <g>
                        <rect
                          x={b.x}
                          y={Math.max(0, b.y - 26)}
                          width={Math.max(80, b.label.length * 10)}
                          height={22}
                          fill={b.color}
                          rx={3}
                        />
                        <text
                          x={b.x + 6}
                          y={Math.max(16, b.y - 10)}
                          fill="white"
                          fontSize={16}
                          fontFamily="Inter, sans-serif"
                          fontWeight={600}
                        >
                          {b.label}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          )}
        </>
      ) : (
        <div className="text-zinc-600 text-sm">No image loaded</div>
      )}
    </div>
  );
}
